import React from 'react';
import './Textarea.css';

export interface TextareaProps extends Omit<
  React.TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size'
> {
  /** Validation state */
  state?: 'default' | 'error' | 'success';
  /** Textarea size */
  size?: 'sm' | 'md' | 'lg';
  /** Full width */
  fullWidth?: boolean;
  /** Resize behavior */
  resize?: 'none' | 'vertical' | 'horizontal' | 'both';
  /** Number of visible text rows (defaults to 3) */
  rows?: number;
}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      state = 'default',
      size = 'md',
      fullWidth = false,
      resize = 'vertical',
      rows = 3,
      disabled,
      readOnly,
      required,
      className = '',
      'aria-invalid': ariaInvalid,
      'aria-required': ariaRequired,
      ...rest
    },
    ref
  ) => {
    const classes = [
      'hi-ui-textarea',
      `hi-ui-textarea-size-${size}`,
      `hi-ui-textarea-resize-${resize}`,
      state !== 'default' && `hi-ui-textarea-state-${state}`,
      fullWidth && 'hi-ui-textarea-full-width',
      disabled && 'hi-ui-textarea-disabled',
      readOnly && 'hi-ui-textarea-readonly',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <textarea
        ref={ref}
        rows={rows}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        className={classes}
        aria-invalid={ariaInvalid ?? (state === 'error' ? true : undefined)}
        aria-required={ariaRequired ?? (required ? true : undefined)}
        {...rest}
      />
    );
  }
);

Textarea.displayName = 'Textarea';
